import React, { useMemo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GridColDef } from '@mui/x-data-grid-pro'
import { useSelector, useDispatch } from 'react-redux'
import { selectSelectedCampaignUuid } from 'state/review'
import { OptionsState } from 'particles/Dropdown'

import {
    makeLocationOptionsByCountrySelector,
    requestLocationOptions,
    selectCityTypesByReviewingCampaignCountry,
    selectFollowerTierFilterOptions,
    selectOccupationOptions,
    selectSortedPartiposterTagsFilter,
} from 'state/constants'
import { selectReviewingCampaignCountry } from 'state/review'
import { FilterItem } from 'components/Shared/MuiTable/FilterAndSortBar'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'
import { EmptySearchResult } from 'particles/MenuItem/Search'
import LocationFilterDropdownList from './LocationFilterDropdownList'
import { TFunction } from 'i18next'
import { GENDER_ENUM } from 'components/Campaign/Editor/CampaignRequirements/GenderRequirement'
import { selectCampaign } from 'state'
import { PLATFORM_ENUM } from 'services/api/constants'
import { selectRegistrationForm } from 'state/registrations'
import _ from 'lodash'
import { TableViewType } from '..'
import { useActiveFilter, useFilterModel } from '../hooks'

interface FilterTooltipProps {
    anchorEl: HTMLDivElement | null
    onClose: () => void
    onChange: (options: OptionsState[], column: FIELDS_TYPE) => void
    column: GridColDef
    viewType?: TableViewType
}
const searchAbleFields: Record<string, boolean> = {
    [FIELDS_TYPE.PARTIPOSTER_TAGS]: true,
}

const getFieldLabel = (t: TFunction, field: FIELDS_TYPE, value: string | number) => {
    switch (field) {
        case FIELDS_TYPE.GENDER: {
            return {
                [GENDER_ENUM.MALE]: t('gender.male'),
                [GENDER_ENUM.FEMALE]: t('gender.female'),
                [GENDER_ENUM.OTHERS]: t('general.option.others'),
            }[value]
        }
        case FIELDS_TYPE.HIGHLIGHT: {
            return {
                true: t('statusLabel.savedPartiposter'),
                false: t('statusLabel.unsavedPartiposter'),
            }[value]
        }
        case FIELDS_TYPE.MEET_REQUIREMENT: {
            return {
                recommended: t('tag.recommended'),
                true: t('submission.table.meetRequirementsLabel'),
                false: t('submission.table.notMeetRequirementsLabel'),
            }[value]
        }
        case FIELDS_TYPE.STATUS: {
            return {
                true: t('statusLabel.viewed'),
                false: t('statusLabel.new'),
                // TODO: Discuss with backened to use a different field name for submissions status
                in_progress: t('statusLabel.inProgress'),
                completed: t('statusLabel.completed'),
                drop_out: t('tag.dropOut'),
                rejected: t('statusLabel.rejected'),
            }[value]
        }
        case FIELDS_TYPE.FAVORITE: {
            return {
                true: t('tag.favourite'),
                false: t('tag.notFavourite'),
            }[value]
        }
        case FIELDS_TYPE.PROFILE_TYPE: {
            return {
                nano: t('tag.nano'),
                micro: t('tag.micro'),
                macro: t('tag.macro'),
                mega: t('tag.mega'),
            }[value]
        }
    }
    return ''
}

const ColumnFilterTooltip: React.FC<FilterTooltipProps> = ({ anchorEl, onClose, column, onChange, viewType }) => {
    const [t] = useTranslation()
    const activeFilters = useActiveFilter(viewType)
    const campaignUuid = useSelector(selectSelectedCampaignUuid)
    const campaign = useSelector(selectCampaign(campaignUuid as string))
    const iGfollowersTiresOptions = useSelector(selectFollowerTierFilterOptions(PLATFORM_ENUM.IG, campaign))
    const fBfollowersTiresOptions = useSelector(selectFollowerTierFilterOptions(PLATFORM_ENUM.FB, campaign))
    const tikTokfollowersTiresOptions = useSelector(selectFollowerTierFilterOptions(PLATFORM_ENUM.TIKTOK, campaign))
    const twitterfollowersTiresOptions = useSelector(selectFollowerTierFilterOptions(PLATFORM_ENUM.TWITTER, campaign))
    const xiaohongshufollowersTiresOptions = useSelector(
        selectFollowerTierFilterOptions(PLATFORM_ENUM.XIAOHONGSHU, campaign),
    )
    const occupationOptions = useSelector(selectOccupationOptions)
    const partiposterTagsFilter = useSelector(selectSortedPartiposterTagsFilter)

    const [searchTags, setSearchTags] = useState<string>('')
    const { field } = column || {}

    const [locationParent, setLocationParent] = useState('')
    const registrationForm = useSelector(selectRegistrationForm)
    const registrationFormBlocksById = useMemo(() => {
        return registrationForm && registrationForm.blocks ? _.keyBy(registrationForm.blocks, 'uuid') : {}
    }, [registrationForm])

    const dispatch = useDispatch()

    const { items: filters = [] } = (useFilterModel(viewType) || {}) as {
        items: FilterItem[]
    }
    const reviewingCampaignCountry = useSelector(selectReviewingCampaignCountry)
    const cityTypes = useSelector(selectCityTypesByReviewingCampaignCountry)

    // Rank 1
    const rank1CityType = cityTypes.find(city => city.rank === 1)
    const idName = rank1CityType ? rank1CityType.id_name : ''
    const locationOptionsByCountrySelector = makeLocationOptionsByCountrySelector('review', idName, undefined)
    const { data: locationParentOptions } = useSelector(locationOptionsByCountrySelector)

    // Rank 2
    const rank2CityType = cityTypes.find(city => city.rank === 2)
    const isMultiLevelLocation = cityTypes.length > 1
    const decodeParentId = (id: string) => id && JSON.parse(id).rank1

    const locationOptions = makeLocationOptionsByCountrySelector(
        'review',
        rank2CityType ? rank2CityType.id_name : '',
        decodeParentId(locationParent),
    )

    const partiposterTagsOptions = useMemo(() => {
        return partiposterTagsFilter.map(option => {
            return {
                label: option.name,
                value: option.uuid,
            }
        })
    }, [partiposterTagsFilter])

    const { isLoading: isOptionsLoading, data: locationChildOptions } = useSelector(locationOptions)

    useEffect(() => {
        if (reviewingCampaignCountry && locationParent && rank2CityType) {
            dispatch(
                requestLocationOptions({
                    country: reviewingCampaignCountry,
                    type_id_name: rank2CityType.id_name,
                    parent: decodeParentId(locationParent),
                }),
            )
        }
    }, [locationParent])

    const filteredLocationChildOptions = useMemo(() => {
        const submissionFilter = activeFilters[`${FIELDS_TYPE.LOCATION}+${decodeParentId(locationParent)}`]
        return locationChildOptions?.filter(option => {
            return submissionFilter && submissionFilter.includes(String(option.id))
        })
    }, [locationChildOptions])

    const columnOptions = useMemo(() => {
        const returnFilter = [] as OptionsState[]
        const findOption = <T,>(options: T[], findOption: (option: T) => boolean) => {
            return options.find(findOption)
        }
        if (field === FIELDS_TYPE.LOCATION && isMultiLevelLocation) {
            // Get all location filters
            for (const filterKey in activeFilters) {
                if (filterKey.includes(FIELDS_TYPE.LOCATION)) {
                    const parentLocationId = filterKey.split('+')[1]
                    const locationOption =
                        locationParentOptions &&
                        findOption(locationParentOptions, option => String(option.id) === parentLocationId)
                    returnFilter.push({
                        label: locationOption?.name || '',
                        value: `{"rank1":"${parentLocationId}","rank2":""}`,
                    })
                }
            }
        } else if (field === FIELDS_TYPE.PARTIPOSTER_TAGS) {
            // Follow tags ordering
            const filter = activeFilters[field as FIELDS_TYPE]
            partiposterTagsOptions.forEach(tag => {
                if (filter && filter.includes(tag.value)) {
                    returnFilter.push(tag)
                }
            })
            returnFilter.unshift({ label: t('submission.tags.noTagYetFilteringOption'), value: 'null' })
        } else {
            const filter = activeFilters[field as FIELDS_TYPE]

            filter &&
                filter.forEach(item => {
                    let option
                    if (field?.includes(FIELDS_TYPE.MCQ)) {
                        const blockUuid = field.split('+')[1]

                        const blockOption = registrationFormBlocksById[blockUuid].options.find(
                            option => option.uuid === item,
                        )

                        if (blockOption) {
                            returnFilter.push({
                                label: blockOption.is_others
                                    ? t('submission.table.otherLabel', 'Other')
                                    : blockOption.option,
                                value: blockOption.uuid,
                            })
                        }
                    } else {
                        switch (field) {
                            case FIELDS_TYPE.GENDER:
                                option = {
                                    label: getFieldLabel(t, field, item.toLowerCase()) || '',
                                    value: item.toLowerCase(),
                                }
                                break
                            case FIELDS_TYPE.HIGHLIGHT:
                            case FIELDS_TYPE.PROFILE_TYPE:
                            case FIELDS_TYPE.STATUS:
                            case FIELDS_TYPE.MEET_REQUIREMENT:
                            case FIELDS_TYPE.FAVORITE:
                                option = {
                                    label: getFieldLabel(t, field, item) || '',
                                    value: item,
                                }
                                break
                            case FIELDS_TYPE.EMPLOYMENT_STATUS:
                                option = {
                                    ...findOption(occupationOptions, option => String(option.id) === item),
                                    value: item,
                                }
                                break
                            case FIELDS_TYPE.IG_FOLLOWERS:
                                option = {
                                    ...findOption(
                                        iGfollowersTiresOptions,
                                        option => String(option.value) === String(item),
                                    ),
                                    value: item,
                                }
                                break
                            case FIELDS_TYPE.FB_FRIENDS:
                                option = {
                                    ...findOption(fBfollowersTiresOptions, option => String(option.value) === item),
                                    value: item,
                                }
                                break
                            case FIELDS_TYPE.TIKTOK_FOLLOWERS:
                                option = {
                                    ...findOption(tikTokfollowersTiresOptions, option => String(option.value) === item),
                                    value: item,
                                }
                                break
                            case FIELDS_TYPE.TWITTER_FOLLOWERS:
                                option = {
                                    ...findOption(
                                        twitterfollowersTiresOptions,
                                        option => String(option.value) === item,
                                    ),
                                    value: item,
                                }
                                break
                            case FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS:
                                option = {
                                    ...findOption(
                                        xiaohongshufollowersTiresOptions,
                                        option => String(option.value) === item,
                                    ),
                                    value: item,
                                }
                                break
                            case FIELDS_TYPE.LOCATION:
                                const locationOption =
                                    locationParentOptions &&
                                    findOption(locationParentOptions, option => String(option.id) === item)
                                option = {
                                    label: locationOption ? locationOption.name : '',
                                    value: `{"rank1":"${item}","rank2":""}`,
                                }
                                break
                            case FIELDS_TYPE.COMPLETED_PROGRESS_COUNT:
                                option = {
                                    label: `${item}`,
                                    value: item,
                                }
                                break
                        }
                        returnFilter.push({
                            label: option?.label || '',
                            value: option?.value || item,
                        })
                    }
                })
        }
        return returnFilter
    }, [field, column, activeFilters, occupationOptions, partiposterTagsOptions])

    const filterOptions = useMemo(() => {
        if (searchTags && columnOptions) {
            return columnOptions.filter(option => option.label.toUpperCase().includes(searchTags.toUpperCase()))
        } else {
            return columnOptions
        }
    }, [columnOptions, searchTags])

    const handleLocationOnChange = (
        options: OptionsState[],
        isChildOption?: boolean | undefined,
        parent?: OptionsState,
    ) => {
        const optionsToUpdate = [...options]
        if (isChildOption) {
            // If parent exists in filters, we want to remove it so that child filter results are decoupled from it
            const parentFilterExists = parent && filters.some(item => item.value === parent.value)
            if (parentFilterExists && parent) optionsToUpdate.push(parent)

            // Then after that then send onChange for option...
            onChange(optionsToUpdate, field as FIELDS_TYPE)
        } else {
            // If single option we can see that location parent is selected
            if (options.length === 1) {
                setLocationParent(options[0].value as string)
            }
            onChange(optionsToUpdate, field as FIELDS_TYPE)
        }
    }

    const handleOnChange = (option: OptionsState) => {
        onChange([option], field as FIELDS_TYPE)
    }

    const getFilterList = (options: { label: string; value: string | number }[]) => {
        if (!options || options.length === 0) {
            return <EmptySearchResult />
        } else {
            return options.map((option, index) => {
                const isSelected = filters.some(
                    filter => filter.value === option.value && filter.label === option.label,
                )
                return (
                    <MenuItem.Checkbox
                        key={`filter-item-${index}`}
                        tabIndex={index}
                        text={option.label}
                        selected={isSelected}
                        onClick={() => {
                            handleOnChange(option)
                        }}
                    />
                )
            })
        }
    }

    return (
        <Menu size="s" open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose}>
            <MenuItem.Title text={t('dropdownMenu.filterByValueHeader').toUpperCase()} />
            {searchAbleFields[field] && (
                <MenuItem.Search
                    onSearch={(value: string) => setSearchTags(value)}
                    searchValue={searchTags}
                    onClearSearch={() => setSearchTags('')}
                    width="100%"
                    placeholder={t('placeholderText.genericSearch')}
                />
            )}
            {field === 'location' && isMultiLevelLocation ? (
                <LocationFilterDropdownList
                    options={filterOptions}
                    selectedOptions={filters}
                    onSelectOption={handleLocationOnChange}
                    childOptions={filteredLocationChildOptions}
                    parentId={locationParent}
                    field={field}
                    onLoadChildren={parentId => setLocationParent(parentId)}
                    isLoading={isOptionsLoading}
                />
            ) : (
                getFilterList(filterOptions)
            )}
        </Menu>
    )
}

export default ColumnFilterTooltip
